/**
 * Iter188 — RegulatoryCheckModal
 * -------------------------------------------------------------------
 * Shared "Mevzuata Göre Kontrol Et" modal used by:
 *   - Formulas.jsx (Iter187 — approved formulas)
 *   - RD.jsx       (Iter188 — Ar-Ge projeleri)
 *
 * KURAL:
 * - Yeni backend / yeni engine / yeni PDF / yeni snapshot YOK.
 * - AI çağrısı YOK (ai_model gönderilmiyor → deterministik akış).
 * - Endpointler:
 *     GET  /regulatory/countries
 *     POST /regulatory/analyze          (immutable snapshot yazar)
 *     GET  /regulatory/analyses/{id}/pdf
 * - Ar-Ge tarafı için `formula_id` olarak `target.formula_id` alanı
 *   (RD sample transfer sonrası `transferred_formula_id`) beklenir.
 *   Aktarılmamış RD projelerinde modal, kullanıcıyı Formüllere aktarım
 *   yapmaya yönlendiren bir uyarı gösterir. Backend'e istek gitmez.
 */
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Modal, Btn, Select } from "@/components/UI";
import { ShieldCheck, FileText, AlertTriangle } from "lucide-react";

export function RegulatoryCheckModal({ open, onClose, target }) {
  const [markets, setMarkets] = useState([]);
  const [market, setMarket] = useState("EU");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  // iter190 — IFRA / Allergen inputs (optional, deterministic)
  const [productCategory, setProductCategory] = useState("");
  const [useType, setUseType] = useState("");
  const [profiles, setProfiles] = useState([]);

  useEffect(() => {
    if (!open) return;
    api.get("/regulatory/countries").then(setMarkets).catch(() => {});
    api.get("/safety/exposure-profiles").then(setProfiles).catch(() => {});
  }, [open]);

  useEffect(() => {
    if (!open) { setResult(null); }
  }, [open, target?.id]);

  const formulaId = target?.formula_id || null;
  const revision = target?.revision || null;
  const label = target?.project_no || target?.code || target?.product_name || "";

  const run = async () => {
    if (!formulaId) return;
    setBusy(true); setResult(null);
    try {
      const r = await api.post("/regulatory/analyze", {
        formula_id: formulaId,
        formula_revision: revision,
        customer_id: target?.customer_id || null,
        product_id: target?.product_id || null,
        country: market,
        language: "tr",
        // iter190 — IFRA/Allergen sub-engine inputs (optional)
        product_category: productCategory || null,
        use_type: useType || null,
        // ai_model intentionally omitted → deterministik / AI çağrısı yok
      });
      setResult(r);
    } catch (e) {
      alert("Hata: " + (e.response?.data?.detail || e.message));
    } finally {
      setBusy(false);
    }
  };

  const openPdf = async () => {
    if (!result?.id) return;
    const APIURL = process.env.REACT_APP_BACKEND_URL + "/api";
    const token = localStorage.getItem("erp_token") || sessionStorage.getItem("erp_token") || "";
    const r = await fetch(`${APIURL}/regulatory/analyses/${result.id}/pdf`,
      { headers: { Authorization: `Bearer ${token}` } });
    const blob = await r.blob();
    window.open(URL.createObjectURL(blob), "_blank");
  };

  return (
    <Modal open={open} onClose={onClose}
           title={`Mevzuata Göre Kontrol Et — ${label}`}
           size="xl">
      <div className="space-y-3" data-testid="regulatory-check-modal">
        {!formulaId ? (
          <div className="p-3 bg-amber-50 border border-amber-300 rounded flex items-start gap-2 text-sm"
               data-testid="regulatory-need-transfer">
            <AlertTriangle size={18} className="text-amber-600 mt-0.5"/>
            <div>
              <div className="font-semibold text-amber-800">Bu proje henüz Formüllere aktarılmamış.</div>
              <div className="text-amber-700 text-xs mt-1">
                Mevzuat Kontrolü aktif bir formül revizyonu üzerinde çalışır.
                Ar-Ge projesini önce <strong>Onayla</strong> ve <strong>Formüllere Aktar</strong> adımlarından
                geçirin; ardından aynı ekrandan Mevzuat Kontrolü yapabilirsiniz.
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className="flex items-end gap-2 flex-wrap">
              <Select label="Hedef Pazar" value={market}
                      onChange={e => setMarket(e.target.value)}
                      data-testid="compliance-market-select">
                {(markets || []).map(c => (
                  <option key={c.code} value={c.code}>
                    {c.name_tr || c.name || c.code} ({c.code})
                  </option>
                ))}
              </Select>
              <Select label="Ürün Kategorisi (IFRA/Allergen)" value={productCategory}
                      onChange={e => setProductCategory(e.target.value)}
                      data-testid="compliance-category-select">
                <option value="">— seçiniz (opsiyonel) —</option>
                {(profiles || []).map(p => (
                  <option key={p.id} value={p.category}>{p.label_tr}</option>
                ))}
              </Select>
              <Select label="Kullanım Tipi" value={useType}
                      onChange={e => setUseType(e.target.value)}
                      data-testid="compliance-usetype-select">
                <option value="">— seçiniz (opsiyonel) —</option>
                <option value="leave-on">leave-on</option>
                <option value="rinse-off">rinse-off</option>
              </Select>
              <Btn onClick={run} disabled={busy} data-testid="compliance-run-btn">
                <ShieldCheck size={12}/> {busy ? "Kontrol ediliyor..." : "Kontrol Et"}
              </Btn>
              {result && (
                <Btn variant="secondary" onClick={openPdf} data-testid="compliance-pdf-btn">
                  <FileText size={12}/> PDF Al
                </Btn>
              )}
            </div>

            {result && (() => {
              const r = result.result || {};
              const findings = r.findings || [];
              const unknown = r.ingredients_unknown || [];
              const overall = r.status || "unknown";
              const overallStyle =
                overall === "red" || overall === "prohibited" ? "bg-rose-100 text-rose-800 ring-rose-300" :
                overall === "yellow" ? "bg-amber-100 text-amber-800 ring-amber-300" :
                overall === "green" || overall === "compliant" ? "bg-emerald-100 text-emerald-800 ring-emerald-300" :
                "bg-slate-100 text-slate-700 ring-slate-300";
              return (
                <div className="space-y-3">
                  <div className={`inline-block px-3 py-1 rounded-full ring-1 text-sm font-semibold ${overallStyle}`}
                       data-testid="compliance-overall-status">
                    Genel: {overall}
                  </div>
                  <div className="text-xs text-slate-500">
                    Analiz No: <span className="font-mono">{result.analysis_no}</span>
                    {" · "}Snapshot: <span className="font-mono">{(result.formula_snapshot || {}).revision}</span>
                    {" · "}Kural sayısı: {r.rules_matched || 0}
                  </div>
                  {findings.length > 0 && (
                    <div className="border rounded overflow-hidden">
                      <table className="w-full text-xs">
                        <thead className="bg-slate-100">
                          <tr>
                            <th className="p-1.5 text-left">Durum</th>
                            <th className="p-1.5 text-left">INCI</th>
                            <th className="p-1.5 text-right">Kullanım</th>
                            <th className="p-1.5 text-right">Limit</th>
                            <th className="p-1.5 text-left">Kural</th>
                            <th className="p-1.5 text-left">Referans</th>
                          </tr>
                        </thead>
                        <tbody>
                          {findings.map((f, i) => {
                            const tone = f.status === "red" || f.status === "prohibited" ? "text-rose-700" :
                              f.status === "yellow" ? "text-amber-700" : "text-emerald-700";
                            return (
                              <tr key={i} className="border-t" data-testid={`compliance-finding-${i}`}>
                                <td className={`p-1.5 font-semibold ${tone}`}>{f.status}</td>
                                <td className="p-1.5">{f.inci}</td>
                                <td className="p-1.5 text-right font-mono">{f.percent != null ? `%${f.percent}` : "—"}</td>
                                <td className="p-1.5 text-right font-mono">{f.max_pct != null ? `%${f.max_pct}` : "—"}</td>
                                <td className="p-1.5">{f.rule_type || "—"}</td>
                                <td className="p-1.5 text-slate-500 text-[10px]">{f.reference || "—"}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                  {unknown.length > 0 && (
                    <div className="p-2 bg-amber-50 border border-amber-200 rounded text-xs"
                         data-testid="compliance-unknown-inci">
                      🟡 Eksik/Bilinmeyen INCI: <span className="font-mono">{unknown.join(", ")}</span>
                      <div className="text-[10px] text-slate-500 mt-1">
                        Bu maddeler için doğrulanmış mevzuat kaydı bulunamadı. Hammadde Kartı INCI bilgilerini tamamlayın.
                      </div>
                    </div>
                  )}
                  <div className="text-[10px] text-slate-400 italic">
                    Bu rapor Mevzuat Ön Kontrol niteliğindedir. AI kullanılmamıştır — kayıtlı formül + Hammadde Kartı + doğrulanmış mevzuat kuralları temel alınmıştır.
                  </div>
                  {/* iter190 — IFRA + Allergen özet bölümü (yalnız veri varsa) */}
                  {(result.result?.ifra?.findings?.length > 0
                    || result.result?.allergen?.findings?.length > 0
                    || (result.result?.ifra?.warnings || []).length > 0) && (
                    <div className="space-y-2 pt-2 border-t" data-testid="compliance-ifra-allergen">
                      {result.result?.ifra?.findings?.length > 0 && (
                        <div>
                          <div className="text-xs font-semibold mb-1">
                            IFRA — {result.result.ifra.amendment} · <span className="font-mono">{result.result.ifra.dataset_version}</span>
                          </div>
                          <table className="w-full text-xs">
                            <thead className="bg-slate-100">
                              <tr>
                                <th className="p-1 text-left">Durum</th>
                                <th className="p-1 text-left">INCI</th>
                                <th className="p-1 text-right">Formül</th>
                                <th className="p-1 text-right">Limit</th>
                                <th className="p-1 text-left">Kural</th>
                              </tr>
                            </thead>
                            <tbody>
                              {result.result.ifra.findings.map((f, i) => (
                                <tr key={i} className="border-t"
                                    data-testid={`ifra-finding-${i}`}>
                                  <td className={`p-1 font-semibold ${f.status === "red" ? "text-rose-700" : f.status === "yellow" ? "text-amber-700" : "text-emerald-700"}`}>{f.status}</td>
                                  <td className="p-1">{f.inci || "—"}</td>
                                  <td className="p-1 text-right font-mono">{f.percent != null ? `%${f.percent}` : "—"}</td>
                                  <td className="p-1 text-right font-mono">{f.max_pct != null ? `%${f.max_pct}` : "—"}</td>
                                  <td className="p-1">{f.kind}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                      {result.result?.allergen?.findings?.length > 0 && (
                        <div>
                          <div className="text-xs font-semibold mb-1">
                            Allergen — {result.result.allergen.regulation} · <span className="font-mono">{result.result.allergen.dataset_version}</span>
                          </div>
                          <table className="w-full text-xs">
                            <thead className="bg-slate-100">
                              <tr>
                                <th className="p-1 text-left">Durum</th>
                                <th className="p-1 text-left">Allergen</th>
                                <th className="p-1 text-right">Toplam</th>
                                <th className="p-1 text-right">Eşik</th>
                                <th className="p-1 text-left">Kural</th>
                              </tr>
                            </thead>
                            <tbody>
                              {result.result.allergen.findings.map((f, i) => (
                                <tr key={i} className="border-t"
                                    data-testid={`allergen-finding-${i}`}>
                                  <td className={`p-1 font-semibold ${f.status === "red" ? "text-rose-700" : f.status === "orange" ? "text-orange-700" : f.status === "yellow" ? "text-amber-700" : "text-emerald-700"}`}>{f.status}</td>
                                  <td className="p-1">{f.inci || "—"}</td>
                                  <td className="p-1 text-right font-mono">{f.total_pct != null ? `%${f.total_pct}` : "—"}</td>
                                  <td className="p-1 text-right font-mono">{f.threshold_pct != null ? `%${f.threshold_pct}` : "—"}</td>
                                  <td className="p-1">{f.kind}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })()}
          </>
        )}
      </div>
    </Modal>
  );
}

export default RegulatoryCheckModal;
